import React from 'react';
import useEmployeesWithoutSalary from '../../hooks/useEmployeesWithoutSalary';
import {
  CircularProgress,
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import { MoneyOff as MoneyOffIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TH, TD, Row, TableWrap, TABLE_STYLE, AVATAR_SX } from '../common/tableStyles';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

// Soft tinted status chip sx (matches the rest of the portal).
const chipSx = (fg, bg) => ({ fontWeight: 700, bgcolor: bg, color: fg, border: 'none' });

const EmployeesWithoutSalary = () => {
  const { data: employees, isLoading, error } = useEmployeesWithoutSalary();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MoneyOffIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#ef4444' }}>Unable to load employees</Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>Error fetching employees: {error.message}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MoneyOffIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Employees Without Salary
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Employees who do not yet have a salary structure configured
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${employees?.length || 0} employees`}
            sx={chipSx(ACCENT, `${ACCENT}14`)}
          />
        </Stack>
      </Paper>

      {/* Table card */}
      <Paper elevation={0} sx={CARD_SX}>
        <TableWrap>
          <table style={TABLE_STYLE}>
            <thead>
              <tr>
                <th style={TH}>Emp No</th>
                <th style={TH}>Name</th>
                <th style={TH}>Email</th>
                <th style={TH}>Role</th>
                <th style={TH}>Department</th>
                <th style={TH}>Mobile</th>
                <th style={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              {employees?.map((emp, index) => (
                <Row
                  key={index}
                  onClick={() => navigate('/payroll', { state: { selectedEmployee: emp, openModal: true } })}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={TD}>
                    <Chip
                      label={emp.employee_no}
                      size="small"
                      sx={chipSx('#16a34a', '#f0fdf4')}
                    />
                  </td>
                  <td style={TD}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={AVATAR_SX}>
                        {emp.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontWeight: '500' }}>
                        {emp.name || 'N/A'}
                      </Typography>
                    </Box>
                  </td>
                  <td style={TD}>{emp.email || 'N/A'}</td>
                  <td style={TD}>{emp.designation || 'N/A'}</td>
                  <td style={TD}>{emp.department || 'N/A'}</td>
                  <td style={TD}>{emp.mobile_number || 'N/A'}</td>
                  <td style={TD}>
                    <Chip
                      label={emp.status || 'Active'}
                      size="small"
                      sx={emp.status === 'Resigned'
                        ? chipSx('#ef4444', '#fef2f2')
                        : chipSx(ACCENT, `${ACCENT}14`)}
                    />
                  </td>
                </Row>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Paper>
    </Box>
  );
};

export default EmployeesWithoutSalary;
